/**
 * Pure statistical helpers for the Report page.
 *
 * Inputs are always already-filtered transaction arrays (scope / expenseType /
 * paidBy applied upstream). All functions are deterministic and side-effect free
 * so they can be exercised from unit tests.
 */

import { UNCATEGORIZED_LABEL } from '@/lib/constants'
import { getTransactionDisplayName } from '@/lib/utils'
import { clusterStoreNames } from '@/lib/fuzzy-match'
import type { Transaction } from '@/lib/api'

// --- Types ---

export type CategoryBaseline = {
  parentName: string
  /** Mean monthly spend across the lookback window (excludes current month). */
  mean: number
  /** Population std-dev across the lookback window. */
  stdDev: number
  monthsObserved: number
}

export type CategoryDeviation = {
  parentName: string
  current: number
  baselineMean: number
  /** current - baselineMean */
  delta: number
  /** (current - baselineMean) / baselineMean, null if baselineMean is 0 */
  deltaPct: number | null
  /** Standard deviations from the mean. null when stdDev is 0 or no history. */
  zScore: number | null
  level: DeviationLevel
  /**
   * Whether this category has historically been driven by recurring (fixed)
   * transactions. UIs should suppress "少なめ/やや多い" framing for
   * mostly-fixed categories — variation in 家賃 is not actionable.
   */
  classification: CategoryClassification
}

export type DeviationLevel = 'low' | 'normal' | 'high' | 'very-high'

export type CategoryClassification = 'mostly-fixed' | 'mostly-variable' | 'mixed'

export type FrequentMerchant = {
  storeName: string
  visitCount: number
  totalAmount: number
  avgPerVisit: number
  parentCategoryName: string | null
  parentCategoryColor: string | null
}

export type DailyPacePoint = {
  /** 1..31 */
  day: number
  /** Cumulative current-month spend on this day. Null after today (future). */
  current: number | null
  /** Cumulative baseline (mean of past months) spend on this day. */
  baseline: number
  /** End-of-month projection from today's pace. Null until we have 1+ days. */
  projected: number | null
}

export type DayOfWeekStat = {
  /** 1=Mon .. 7=Sun (ISO style, since the rest of the app starts weeks on Mon) */
  dow: number
  label: string
  total: number
  count: number
  occurrences: number
  avgPerOccurrence: number
}

export type Insight = {
  id: string
  level: 'info' | 'warn' | 'good'
  title: string
  detail?: string
}

export type MonthlyStatistics = {
  total: number
  transactionCount: number
  /** Days in the month that had at least one transaction */
  spendingDays: number
  /** Total days in the month (28..31) */
  daysInMonth: number
  /** Days elapsed in the month so far (capped at daysInMonth). */
  daysElapsed: number
  /** Average per spending day */
  avgPerSpendingDay: number
  /** Daily pace (total / daysElapsed) for projection */
  dailyPace: number
  /** Projected total at end of month (only when month is in progress). */
  projected: number | null
  /** Largest single transaction in the month, or null when empty. */
  largestTx: { id: string; date: string; storeName: string; amount: number } | null
}

const DOW_LABELS = ['月', '火', '水', '木', '金', '土', '日'] as const

// --- Date helpers ---

export function parseYearMonth(yearMonth: string): { year: number; month: number } {
  const [year, month] = yearMonth.split('-').map(Number)
  return { year, month }
}

export function daysInMonth(yearMonth: string): number {
  const { year, month } = parseYearMonth(yearMonth)
  return new Date(year, month, 0).getDate()
}

/**
 * Day of month (1..31) elapsed in `yearMonth` as of `today`.
 *
 * - Past month → full days
 * - Future month → 0
 * - Current month → today's date
 */
export function daysElapsedInMonth(yearMonth: string, today: Date = new Date()): number {
  const total = daysInMonth(yearMonth)
  const { year, month } = parseYearMonth(yearMonth)
  const ty = today.getFullYear()
  const tm = today.getMonth() + 1
  if (year < ty || (year === ty && month < tm)) return total
  if (year > ty || (year === ty && month > tm)) return 0
  return Math.min(today.getDate(), total)
}

/** ISO-style day of week (1=Mon..7=Sun) for a YYYY-MM-DD date string. */
export function isoDow(dateStr: string): number {
  const d = new Date(dateStr + 'T00:00:00')
  const js = d.getDay() // 0=Sun..6=Sat
  return js === 0 ? 7 : js
}

// --- Per-month aggregation helpers (used by baseline + current) ---

/** Aggregate transactions into a parent-category → amount map. */
export function aggregateByParentCategory(transactions: Transaction[]): Map<string, number> {
  const map = new Map<string, number>()
  for (const tx of transactions) {
    for (const item of tx.items) {
      const parent = item.parent_category_name ?? UNCATEGORIZED_LABEL
      map.set(parent, (map.get(parent) ?? 0) + item.amount)
    }
  }
  return map
}

/** Total spend for a transactions array. */
export function sumTotal(transactions: Transaction[]): number {
  let total = 0
  for (const tx of transactions) total += tx.total_amount
  return total
}

// --- Category drilldown (used by the detail-sheet) ---

export type CategorySubcategory = { name: string; amount: number; count: number }
export type CategoryDrilldown = {
  name: string
  amount: number
  subcategories: CategorySubcategory[]
}

/**
 * Build a parent-category → drilldown map for the bottom-sheet detail view.
 * Subcategories are sorted by amount desc; subcategory `count` reflects the
 * number of distinct transactions that contributed to it.
 */
export function buildCategoryDrilldown(transactions: Transaction[]): Map<string, CategoryDrilldown> {
  const map = new Map<
    string,
    { amount: number; subs: Map<string, { amount: number; tx: Set<string> }> }
  >()
  for (const tx of transactions) {
    for (const item of tx.items) {
      const parent = item.parent_category_name ?? UNCATEGORIZED_LABEL
      const sub = item.category_name ?? UNCATEGORIZED_LABEL
      const entry = map.get(parent) ?? { amount: 0, subs: new Map() }
      entry.amount += item.amount
      const subEntry = entry.subs.get(sub) ?? { amount: 0, tx: new Set<string>() }
      subEntry.amount += item.amount
      subEntry.tx.add(tx.id)
      entry.subs.set(sub, subEntry)
      map.set(parent, entry)
    }
  }
  const out = new Map<string, CategoryDrilldown>()
  for (const [name, e] of map) {
    out.set(name, {
      name,
      amount: e.amount,
      subcategories: Array.from(e.subs.entries())
        .map(([n, s]) => ({ name: n, amount: s.amount, count: s.tx.size }))
        .sort((a, b) => b.amount - a.amount),
    })
  }
  return out
}

// --- Baseline ---

/**
 * Compute per-parent-category baseline (mean / stdDev) over a set of past
 * months. Each month contributes one observation per category — months in
 * which the category had zero spend count as 0, since "we usually spend nothing
 * on this" is a meaningful baseline.
 *
 * `pastMonthsTransactions` should be ordered oldest→newest and *not* include the
 * current month being analyzed.
 */
export function computeCategoryBaselines(
  pastMonthsTransactions: Transaction[][],
): Map<string, CategoryBaseline> {
  const result = new Map<string, CategoryBaseline>()
  const monthsObserved = pastMonthsTransactions.length
  if (monthsObserved === 0) return result

  const allCategories = new Set<string>()
  const perMonth: Array<Map<string, number>> = pastMonthsTransactions.map((txs) => {
    const m = aggregateByParentCategory(txs)
    for (const k of m.keys()) allCategories.add(k)
    return m
  })

  for (const cat of allCategories) {
    const observations = perMonth.map((m) => m.get(cat) ?? 0)
    const mean = observations.reduce((s, v) => s + v, 0) / monthsObserved
    const variance =
      observations.reduce((s, v) => s + (v - mean) ** 2, 0) / monthsObserved
    const stdDev = Math.sqrt(variance)
    result.set(cat, { parentName: cat, mean, stdDev, monthsObserved })
  }

  return result
}

function levelFromZScore(z: number | null, deltaPct: number | null): DeviationLevel {
  if (z !== null) {
    if (z >= 2) return 'very-high'
    if (z >= 1) return 'high'
    if (z <= -1) return 'low'
    return 'normal'
  }
  // Fall back to %-based threshold when stdDev is 0 (history is flat)
  if (deltaPct === null) return 'normal'
  if (deltaPct >= 0.5) return 'very-high'
  if (deltaPct >= 0.2) return 'high'
  if (deltaPct <= -0.2) return 'low'
  return 'normal'
}

/**
 * Classify each parent category as mostly-fixed (≥70% of past spend came from
 * recurring transactions), mostly-variable (≤30%), or mixed. Used to suppress
 * deviation framing for fixed categories like 家賃.
 */
export function classifyCategoriesByExpenseType(
  pastMonthsTransactions: Transaction[][],
): Map<string, CategoryClassification> {
  const totals = new Map<string, { fixed: number; total: number }>()
  for (const txs of pastMonthsTransactions) {
    for (const tx of txs) {
      const isFixed = tx.input_method === 'recurring'
      for (const item of tx.items) {
        const parent = item.parent_category_name ?? UNCATEGORIZED_LABEL
        const e = totals.get(parent) ?? { fixed: 0, total: 0 }
        e.total += item.amount
        if (isFixed) e.fixed += item.amount
        totals.set(parent, e)
      }
    }
  }
  const out = new Map<string, CategoryClassification>()
  for (const [name, { fixed, total }] of totals) {
    if (total === 0) {
      out.set(name, 'mostly-variable')
      continue
    }
    const ratio = fixed / total
    if (ratio >= 0.7) out.set(name, 'mostly-fixed')
    else if (ratio <= 0.3) out.set(name, 'mostly-variable')
    else out.set(name, 'mixed')
  }
  return out
}

/**
 * Compare current-month per-category totals against baselines.
 * Returns one row per category that exists in EITHER current OR baseline,
 * sorted by absolute delta (largest swings first).
 *
 * `classifications` can be passed to mark categories as mostly-fixed; for
 * those rows the deviation level is forced to 'normal' so UIs don't surface
 * "少なめ" framing on a category whose variation is not under user control.
 */
export function computeCategoryDeviations(
  currentMonthTotals: Map<string, number>,
  baselines: Map<string, CategoryBaseline>,
  classifications: Map<string, CategoryClassification> = new Map(),
): CategoryDeviation[] {
  const allKeys = new Set<string>([...currentMonthTotals.keys(), ...baselines.keys()])
  const out: CategoryDeviation[] = []
  for (const key of allKeys) {
    const current = currentMonthTotals.get(key) ?? 0
    const baseline = baselines.get(key)
    const baselineMean = baseline?.mean ?? 0
    const delta = current - baselineMean
    const deltaPct = baselineMean > 0 ? delta / baselineMean : null
    const zScore =
      baseline && baseline.stdDev > 0 ? (current - baseline.mean) / baseline.stdDev : null
    const classification = classifications.get(key) ?? 'mostly-variable'
    const baseLevel = levelFromZScore(zScore, deltaPct)
    const level: DeviationLevel = classification === 'mostly-fixed' ? 'normal' : baseLevel
    out.push({
      parentName: key,
      current,
      baselineMean,
      delta,
      deltaPct,
      zScore,
      level,
      classification,
    })
  }
  return out.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
}

// --- Frequent merchants (latte factor) ---

/**
 * Top stores ranked by visit count (with a minimum threshold) — these are the
 * "death by a thousand cuts" candidates. Stores visited only once are excluded
 * because a single trip is not a habit.
 *
 * Receipt-derived store names contain transcription noise (full/half-width,
 * punctuation, branch suffixes) so we cluster variants via edit-distance and
 * shared-prefix matching — see `clusterStoreNames` — before aggregating.
 */
export function computeFrequentMerchants(
  transactions: Transaction[],
  options: { minVisits?: number; topN?: number } = {},
): FrequentMerchant[] {
  const { minVisits = 3, topN = 5 } = options

  // Phase 1 — collect raw store names per transaction so we can fuzzy-cluster.
  // 店名が無い取引は「よく行く店」の候補にしない (品目フォールバックは除外)。
  type Raw = { tx: Transaction; rawName: string }
  const raw: Raw[] = []
  for (const tx of transactions) {
    if (!tx.store_name) continue
    raw.push({ tx, rawName: tx.store_name })
  }
  if (raw.length === 0) return []

  const canonicalByRaw = clusterStoreNames(raw.map((r) => r.rawName))

  const map = new Map<
    string,
    {
      visits: Set<string>
      total: number
      catCounts: Map<string, { name: string; color: string | null; weight: number }>
    }
  >()

  for (const { tx, rawName } of raw) {
    const name = canonicalByRaw.get(rawName) ?? rawName
    const entry = map.get(name) ?? {
      visits: new Set<string>(),
      total: 0,
      catCounts: new Map(),
    }
    entry.visits.add(tx.id)
    entry.total += tx.total_amount
    for (const item of tx.items) {
      if (!item.parent_category_name) continue
      const slot = entry.catCounts.get(item.parent_category_name) ?? {
        name: item.parent_category_name,
        color: item.parent_category_color,
        weight: 0,
      }
      slot.weight += item.amount
      entry.catCounts.set(item.parent_category_name, slot)
    }
    map.set(name, entry)
  }

  const list: FrequentMerchant[] = []
  for (const [storeName, e] of map) {
    if (e.visits.size < minVisits) continue
    let topCat: { name: string; color: string | null } | null = null
    let topWeight = -1
    for (const slot of e.catCounts.values()) {
      if (slot.weight > topWeight) {
        topWeight = slot.weight
        topCat = { name: slot.name, color: slot.color }
      }
    }
    list.push({
      storeName,
      visitCount: e.visits.size,
      totalAmount: e.total,
      avgPerVisit: Math.round(e.total / e.visits.size),
      parentCategoryName: topCat?.name ?? null,
      parentCategoryColor: topCat?.color ?? null,
    })
  }

  return list
    .sort((a, b) => b.visitCount - a.visitCount || b.totalAmount - a.totalAmount)
    .slice(0, topN)
}

// --- Daily pace ---

/**
 * Daily cumulative pace for the current month, with a baseline curve from the
 * past months' average daily spending.
 *
 * Returns one entry per day in the month. `current` is null for future days so
 * the line chart can stop drawing past today.
 */
export function computeDailyPace(
  yearMonth: string,
  currentMonthTransactions: Transaction[],
  pastMonthsTransactions: Transaction[][],
  today: Date = new Date(),
): DailyPacePoint[] {
  const total = daysInMonth(yearMonth)
  const elapsed = daysElapsedInMonth(yearMonth, today)

  // Current month: daily totals
  const dailyCurrent = new Array<number>(total + 1).fill(0)
  for (const tx of currentMonthTransactions) {
    const day = Number(tx.date.slice(8, 10))
    if (day >= 1 && day <= total) {
      dailyCurrent[day] += tx.total_amount
    }
  }

  // Baseline: average per-day-of-month across past months. We normalize each
  // month by its own length so a 30-day month doesn't drag down day-31 averages.
  const baselineByDay = new Array<number>(total + 1).fill(0)
  if (pastMonthsTransactions.length > 0) {
    const accum = new Array<number>(total + 1).fill(0)
    const counts = new Array<number>(total + 1).fill(0)
    for (const monthTxs of pastMonthsTransactions) {
      // First infer the month length for this batch: max day-of-month observed
      // suffices for our purposes here (we only need an approximate denominator).
      let monthLen = 28
      const perDay = new Array<number>(32).fill(0)
      for (const tx of monthTxs) {
        const day = Number(tx.date.slice(8, 10))
        if (day >= 1 && day <= 31) {
          perDay[day] += tx.total_amount
          if (day > monthLen) monthLen = day
        }
      }
      // Distribute days that actually exist in that month into our slots,
      // scaling day-index linearly to handle 28..31 day variations.
      for (let d = 1; d <= total; d++) {
        const sourceDay = Math.min(monthLen, Math.max(1, Math.round((d / total) * monthLen)))
        accum[d] += perDay[sourceDay]
        counts[d] += 1
      }
    }
    for (let d = 1; d <= total; d++) {
      baselineByDay[d] = counts[d] > 0 ? accum[d] / counts[d] : 0
    }
  }

  let cumCurrent = 0
  let cumBaseline = 0
  const out: DailyPacePoint[] = []
  for (let d = 1; d <= total; d++) {
    cumCurrent += dailyCurrent[d]
    cumBaseline += baselineByDay[d]
    const isFuture = d > elapsed
    const projected =
      elapsed > 0 && d === total ? Math.round((cumCurrent / Math.max(elapsed, 1)) * total) : null
    out.push({
      day: d,
      current: isFuture ? null : Math.round(cumCurrent),
      baseline: Math.round(cumBaseline),
      projected,
    })
  }

  // Replace projected on the last entry once for charting convenience
  if (elapsed > 0 && elapsed < total) {
    const projected = Math.round((cumCurrent / elapsed) * total)
    out[total - 1] = { ...out[total - 1], projected }
  }
  return out
}

// --- Day of week ---

export function computeDayOfWeek(
  yearMonth: string,
  transactions: Transaction[],
): DayOfWeekStat[] {
  const totals = new Array<number>(8).fill(0)
  const counts = new Array<number>(8).fill(0)
  for (const tx of transactions) {
    const dow = isoDow(tx.date)
    totals[dow] += tx.total_amount
    counts[dow] += 1
  }
  // How many of each weekday occur in this month?
  const occurrences = new Array<number>(8).fill(0)
  const total = daysInMonth(yearMonth)
  const { year, month } = parseYearMonth(yearMonth)
  for (let d = 1; d <= total; d++) {
    const js = new Date(year, month - 1, d).getDay()
    const dow = js === 0 ? 7 : js
    occurrences[dow] += 1
  }
  const out: DayOfWeekStat[] = []
  for (let dow = 1; dow <= 7; dow++) {
    const occ = Math.max(occurrences[dow], 1)
    out.push({
      dow,
      label: DOW_LABELS[dow - 1],
      total: totals[dow],
      count: counts[dow],
      occurrences: occurrences[dow],
      avgPerOccurrence: Math.round(totals[dow] / occ),
    })
  }
  return out
}

// --- Monthly summary ---

export function computeMonthlyStatistics(
  yearMonth: string,
  transactions: Transaction[],
  today: Date = new Date(),
): MonthlyStatistics {
  const total = daysInMonth(yearMonth)
  const elapsed = daysElapsedInMonth(yearMonth, today)
  const sum = sumTotal(transactions)
  const dates = new Set<string>()
  let largest: MonthlyStatistics['largestTx'] = null
  for (const tx of transactions) {
    dates.add(tx.date)
    if (!largest || tx.total_amount > largest.amount) {
      largest = {
        id: tx.id,
        date: tx.date,
        storeName: getTransactionDisplayName(tx),
        amount: tx.total_amount,
      }
    }
  }
  const spendingDays = dates.size
  const dailyPace = elapsed > 0 ? sum / elapsed : 0
  const projected = elapsed > 0 && elapsed < total ? Math.round(dailyPace * total) : null
  return {
    total: sum,
    transactionCount: transactions.length,
    spendingDays,
    daysInMonth: total,
    daysElapsed: elapsed,
    avgPerSpendingDay: spendingDays > 0 ? Math.round(sum / spendingDays) : 0,
    dailyPace: Math.round(dailyPace),
    projected,
    largestTx: largest,
  }
}

// --- Insights ---

export type InsightInputs = {
  yearMonth: string
  stats: MonthlyStatistics
  baselineTotal: number
  deviations: CategoryDeviation[]
  frequent: FrequentMerchant[]
  dayOfWeek: DayOfWeekStat[]
  /** Budget rows relevant to current scope; pass empty array when none. */
  budgetOverages: Array<{ category_name: string; over: number }>
}

/**
 * Build a short ranked list of insights. We deliberately keep this list small
 * (≤6) and ordered by importance — the goal is to surface the 3-5 things the
 * user should actually notice.
 */
export function generateInsights(input: InsightInputs): Insight[] {
  const out: Insight[] = []
  const { stats, baselineTotal, deviations, frequent, dayOfWeek, budgetOverages } = input

  // 1. Total vs baseline
  if (baselineTotal > 0 && stats.total > 0) {
    const delta = stats.total - baselineTotal
    const pct = delta / baselineTotal
    if (Math.abs(pct) >= 0.1) {
      out.push({
        id: 'total-vs-baseline',
        level: pct >= 0.2 ? 'warn' : pct >= 0 ? 'info' : 'good',
        title:
          pct >= 0
            ? `今月の支出は通常より +${Math.round(pct * 100)}%`
            : `今月の支出は通常より ${Math.round(pct * 100)}%`,
        detail: `6ヶ月平均 ${formatYen(baselineTotal)} に対し ${formatYen(stats.total)}（${
          delta >= 0 ? '+' : ''
        }${formatYen(delta)}）`,
      })
    }
  }

  // 2. Budget overages
  for (const over of budgetOverages.slice(0, 2)) {
    out.push({
      id: `budget-${over.category_name}`,
      level: 'warn',
      title: `予算オーバー: ${over.category_name}`,
      detail: `${formatYen(over.over)} 超過しています`,
    })
  }

  // 3. Top category swings (high/very-high only, and never on a mostly-fixed
  // category — fluctuations there aren't actionable)
  const topHigh = deviations.find(
    (d) =>
      (d.level === 'high' || d.level === 'very-high') &&
      d.delta > 0 &&
      d.classification !== 'mostly-fixed',
  )
  if (topHigh) {
    const pctText = topHigh.deltaPct !== null ? ` (+${Math.round(topHigh.deltaPct * 100)}%)` : ''
    out.push({
      id: `cat-high-${topHigh.parentName}`,
      level: topHigh.level === 'very-high' ? 'warn' : 'info',
      title: `${topHigh.parentName} が通常より多い`,
      detail: `+${formatYen(topHigh.delta)}${pctText} (平均 ${formatYen(topHigh.baselineMean)})`,
    })
  }

  // 4. Latte factor: top frequent merchant
  if (frequent.length > 0) {
    const top = frequent[0]
    out.push({
      id: `frequent-${top.storeName}`,
      level: 'info',
      title: `${top.storeName} に ${top.visitCount}回`,
      detail: `合計 ${formatYen(top.totalAmount)}（1回あたり ${formatYen(top.avgPerVisit)}）`,
    })
  }

  // 5. Weekend skew
  const weekday = dayOfWeek
    .filter((d) => d.dow <= 5)
    .reduce((s, d) => s + d.avgPerOccurrence, 0)
  const weekend = dayOfWeek
    .filter((d) => d.dow >= 6)
    .reduce((s, d) => s + d.avgPerOccurrence, 0)
  const weekdayAvg = weekday / 5
  const weekendAvg = weekend / 2
  if (weekdayAvg > 0 && weekendAvg > weekdayAvg * 1.5) {
    const ratio = (weekendAvg / weekdayAvg).toFixed(1)
    out.push({
      id: 'weekend-skew',
      level: 'info',
      title: `週末の支出が平日の ${ratio}倍`,
      detail: `平日平均 ${formatYen(Math.round(weekdayAvg))} / 週末平均 ${formatYen(
        Math.round(weekendAvg),
      )}`,
    })
  }

  // 6. Big single transaction (only if it dominates)
  if (stats.largestTx && stats.total > 0) {
    const share = stats.largestTx.amount / stats.total
    if (share >= 0.3 && stats.transactionCount >= 3) {
      out.push({
        id: 'big-tx',
        level: 'info',
        title: `${stats.largestTx.storeName} の単発が大きい`,
        detail: `${formatYen(stats.largestTx.amount)}（月支出の ${Math.round(share * 100)}%）`,
      })
    }
  }

  return out.slice(0, 6)
}

function formatYen(amount: number): string {
  return `¥${Math.round(amount).toLocaleString()}`
}
